"use client";

import { useEffect, useRef, useState } from "react";
import { PolarGrid, RadialBar, RadialBarChart } from "recharts";
import { ChartContainer, type ChartConfig } from "@/components/ui/chart";
import { cn } from "@/src/lib/utils";

interface PipelineLoaderProps {
  /** 0..100 */
  percent: number;
  size?: number;
  className?: string;
  /** Вызывается один раз, когда плавный прогресс докрутился до 100. */
  onComplete?: () => void;
}

const RING_COLOR = "#7B3FF2";

const chartConfig = {
  value: { label: "progress", color: RING_COLOR },
} satisfies ChartConfig;

/**
 * Плавно догоняет target через requestAnimationFrame — непрерывно, без рывков
 * на смене этапов. Возвращает текущее интерполированное значение.
 */
function useSmoothValue(target: number, speed = 0.06): number {
  const [value, setValue] = useState(target);
  const valueRef = useRef(target);
  const rafRef = useRef(0);

  useEffect(() => {
    cancelAnimationFrame(rafRef.current);
    const tick = () => {
      const cur = valueRef.current;
      const diff = target - cur;
      if (Math.abs(diff) < 0.3) {
        valueRef.current = target;
        setValue(target);
        return;
      }
      // движемся к цели с минимальным шагом, чтобы всегда дойти и не зависнуть
      const step = Math.sign(diff) * Math.max(0.3, Math.abs(diff) * speed);
      const next = Math.abs(step) >= Math.abs(diff) ? target : cur + step;
      valueRef.current = next;
      setValue(next);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, speed]);

  return value;
}

/**
 * Лоадер генерации: круглый прогресс-бар (как у credibility) с плавной анимацией
 * кольца и цифр, внутри — видео. Размер фиксированный 172.
 */
export default function PipelineLoader({
  percent,
  size = 172,
  className,
  onComplete,
}: PipelineLoaderProps) {
  const target = Math.min(100, Math.max(0, percent));
  const p = useSmoothValue(target);

  // Когда плавный прогресс реально дошёл до 100 — сообщаем наверх (показать данные).
  const completedRef = useRef(false);
  useEffect(() => {
    if (target >= 100 && p >= 100 && !completedRef.current) {
      completedRef.current = true;
      onComplete?.();
    }
  }, [target, p, onComplete]);

  const startAngle = 90;
  const endAngle = 90 - (p / 100) * 360;
  const chartData = [{ name: "progress", value: p, fill: RING_COLOR }];

  return (
    <div
      className={cn("relative inline-flex items-center justify-center overflow-hidden", className)}
      style={{ width: size, height: size }}
    >
      <ChartContainer
        config={chartConfig}
        className="aspect-square"
        style={{ width: size, height: size }}
      >
        <RadialBarChart
          data={chartData}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={70}
          outerRadius={80}
        >
          <PolarGrid
            gridType="circle"
            radialLines={false}
            stroke="none"
            className="first:fill-muted last:fill-background"
            polarRadius={[76, 64]}
          />
          {/* собственную анимацию recharts выключаем — плавность даёт useSmoothValue */}
          <RadialBar dataKey="value" background cornerRadius={10} isAnimationActive={false} />
        </RadialBarChart>
      </ChartContainer>

      {/* видео + проценты по центру, внутри кольца */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="relative flex items-center justify-center overflow-hidden rounded-full w-[60%] h-[60%]">
          <video
            className="absolute inset-0 h-full w-full object-cover scale-[1.05]"
            autoPlay
            loop
            muted
            playsInline
            aria-hidden
          >
            {/* mp4 первым — iOS Safari не проигрывает WebM (лоадер был пустой на iPhone) */}
            <source src="/pipeline-loader.mp4" type="video/mp4" />
            <source src="/pipeline-loader.webm" type="video/webm" />
          </video>
          <span className="absolute font-inter text-2xl font-semibold text-white tabular-nums drop-shadow-md">
            {Math.round(p)}%
          </span>
        </div>
      </div>
    </div>
  );
}
